import { useState } from "react";
import {
    DAY_NAMES,
    GRID_END_MINUTES,
    GRID_START_MINUTES,
    mergeAvailabilityWindows,
    minutesToTime,
    SLOT_MINUTES,
    timeToMinutes,
} from "../../utils/availabilitySchedule";
import styles from "./WeeklyAvailabilityEditor.module.css";
import { AnimatedReveal } from "@iq/models"

const SLOT_HEIGHT = 28;
const SLOT_COUNT = (GRID_END_MINUTES - GRID_START_MINUTES) / SLOT_MINUTES;

function formatGridTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const suffix = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 || 12;
    return `${displayHour} ${suffix}`;
}

function formatBlockTime(value) {
    const minutes = timeToMinutes(value);
    if (minutes === null) return value;
    const hours = Math.floor(minutes / 60);
    const suffix = hours >= 12 ? "PM" : "AM";
    return `${hours % 12 || 12}:${String(minutes % 60).padStart(2, "0")} ${suffix}`;
}

function getSlotFromPointer(event, dayBody) {
    const bounds = dayBody.getBoundingClientRect();
    return Math.max(0, Math.min(SLOT_COUNT - 1, Math.floor((event.clientY - bounds.top) / SLOT_HEIGHT)));
}

function WeeklyAvailabilityEditor({ days, onChange }) {
    const [interaction, setInteraction] = useState(null);

    const updateDayWindows = (dayOfWeek, windows, shouldMerge = true) => {
        onChange(days.map((day) => (
            day.day_of_week === dayOfWeek
                ? { ...day, windows: shouldMerge ? mergeAvailabilityWindows(windows) : windows }
                : day
        )));
    };

    const addWindow = (dayOfWeek, window) => {
        const day = days.find((item) => item.day_of_week === dayOfWeek);
        updateDayWindows(dayOfWeek, [...(day?.windows ?? []), window]);
    };

    const replaceWindow = (dayOfWeek, windowIndex, window, shouldMerge = true) => {
        const day = days.find((item) => item.day_of_week === dayOfWeek);
        const windows = (day?.windows ?? []).map((item, index) => index === windowIndex ? window : item);
        updateDayWindows(dayOfWeek, windows, shouldMerge);
    };

    const removeWindow = (dayOfWeek, windowIndex) => {
        const day = days.find((item) => item.day_of_week === dayOfWeek);
        updateDayWindows(dayOfWeek, (day?.windows ?? []).filter((_, index) => index !== windowIndex));
    };

    const startCreating = (event, dayOfWeek) => {
        if (event.button !== 0) return;
        const slot = getSlotFromPointer(event, event.currentTarget);
        event.currentTarget.setPointerCapture(event.pointerId);
        setInteraction({
            type: "create",
            dayOfWeek,
            anchorSlot: slot,
            preview: { startSlot: slot, endSlot: slot + 1 },
        });
    };

    const updateCreating = (event, dayOfWeek) => {
        if (interaction?.type !== "create" || interaction.dayOfWeek !== dayOfWeek) return;
        const slot = getSlotFromPointer(event, event.currentTarget);
        setInteraction((current) => ({
            ...current,
            preview: {
                startSlot: Math.min(current.anchorSlot, slot),
                endSlot: Math.max(current.anchorSlot, slot) + 1,
            },
        }));
    };

    const finishCreating = () => {
        if (interaction?.type !== "create") return;
        addWindow(interaction.dayOfWeek, {
            start_time: minutesToTime(GRID_START_MINUTES + interaction.preview.startSlot * SLOT_MINUTES),
            end_time: minutesToTime(GRID_START_MINUTES + interaction.preview.endSlot * SLOT_MINUTES),
        });
        setInteraction(null);
    };

    const startAdjusting = (event, dayOfWeek, windowIndex, window, type) => {
        if (event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        setInteraction({
            type,
            dayOfWeek,
            windowIndex,
            pointerY: event.clientY,
            originalStart: timeToMinutes(window.start_time),
            originalEnd: timeToMinutes(window.end_time),
            previewWindow: window,
        });
    };

    const updateAdjusting = (event) => {
        if (!interaction || interaction.type === "create") return;
        const deltaSlots = Math.round((event.clientY - interaction.pointerY) / SLOT_HEIGHT);
        const deltaMinutes = deltaSlots * SLOT_MINUTES;
        let start = interaction.originalStart;
        let end = interaction.originalEnd;

        if (interaction.type === "move") {
            const duration = end - start;
            start = Math.max(GRID_START_MINUTES, Math.min(GRID_END_MINUTES - duration, start + deltaMinutes));
            end = start + duration;
        } else if (interaction.type === "resize-start") {
            start = Math.max(GRID_START_MINUTES, Math.min(end - SLOT_MINUTES, start + deltaMinutes));
        } else {
            end = Math.min(GRID_END_MINUTES, Math.max(start + SLOT_MINUTES, end + deltaMinutes));
        }

        setInteraction((current) => ({
            ...current,
            previewWindow: { start_time: minutesToTime(start), end_time: minutesToTime(end) },
        }));
    };

    const finishAdjusting = () => {
        if (!interaction || interaction.type === "create") return;
        replaceWindow(interaction.dayOfWeek, interaction.windowIndex, interaction.previewWindow);
        setInteraction(null);
    };

    const addMobileWindow = (dayOfWeek) => {
        const day = days.find((item) => item.day_of_week === dayOfWeek);
        const lastEnd = day?.windows?.length
            ? timeToMinutes(day.windows[day.windows.length - 1].end_time)
            : GRID_START_MINUTES;
        const start = lastEnd !== null && lastEnd + 90 <= GRID_END_MINUTES ? lastEnd + SLOT_MINUTES : GRID_START_MINUTES;
        addWindow(dayOfWeek, { start_time: minutesToTime(start), end_time: minutesToTime(start + 60) });
    };

    const hourLabels = [];
    for (let minutes = GRID_START_MINUTES; minutes <= GRID_END_MINUTES; minutes += 60) {
        hourLabels.push(minutes);
    }

    return (
        <AnimatedReveal stagger={50}>
            <div className={styles.grid} aria-label="Weekly availability grid">
                <div className={styles.corner} />
                {DAY_NAMES.map((dayName) => (
                    <div className={styles.dayHeading} key={dayName}>{dayName.slice(0, 3)}</div>
                ))}

                <div className={styles.times} style={{ height: SLOT_COUNT * SLOT_HEIGHT }}>
                    {hourLabels.map((minutes) => (
                        <span
                            key={minutes}
                            style={{ top: ((minutes - GRID_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT }}
                        >
                            {formatGridTime(minutes)}
                        </span>
                    ))}
                </div>

                {days.map((day) => (
                    <div
                        className={styles.dayBody}
                        key={day.day_of_week}
                        style={{ height: SLOT_COUNT * SLOT_HEIGHT }}
                        onPointerDown={(event) => startCreating(event, day.day_of_week)}
                        onPointerMove={(event) => updateCreating(event, day.day_of_week)}
                        onPointerUp={finishCreating}
                        onPointerCancel={() => setInteraction(null)}
                    >
                        {day.windows.map((window, windowIndex) => {
                            const isAdjusting = interaction?.dayOfWeek === day.day_of_week
                                && interaction?.windowIndex === windowIndex
                                && interaction.type !== "create";
                            const displayedWindow = isAdjusting ? interaction.previewWindow : window;
                            const rawStart = timeToMinutes(displayedWindow.start_time);
                            const rawEnd = timeToMinutes(displayedWindow.end_time);
                            if (rawStart === null || rawEnd === null) return null;
                            const start = Math.max(GRID_START_MINUTES, rawStart);
                            const end = Math.min(GRID_END_MINUTES, rawEnd);
                            if (start >= end) return null;

                            return (
                                <div
                                    className={`${styles.block}${isAdjusting ? ` ${styles.active}` : ""}`}
                                    key={`${window.start_time}-${window.end_time}-${windowIndex}`}
                                    style={{
                                        top: ((start - GRID_START_MINUTES) / SLOT_MINUTES) * SLOT_HEIGHT,
                                        height: ((end - start) / SLOT_MINUTES) * SLOT_HEIGHT,
                                    }}
                                    onPointerDown={(event) => startAdjusting(event, day.day_of_week, windowIndex, window, "move")}
                                    onPointerMove={updateAdjusting}
                                    onPointerUp={finishAdjusting}
                                    onPointerCancel={() => setInteraction(null)}
                                    data-reveal
                                >
                                    <button
                                        className={`${styles.resize} ${styles.resizeStart}`}
                                        type="button"
                                        aria-label={`Resize ${DAY_NAMES[day.day_of_week]} availability start`}
                                        onPointerDown={(event) => startAdjusting(event, day.day_of_week, windowIndex, window, "resize-start")}
                                    />
                                    <span>{formatBlockTime(displayedWindow.start_time)}</span>
                                    <span>{formatBlockTime(displayedWindow.end_time)}</span>
                                    <button
                                        className={styles.remove}
                                        type="button"
                                        title="Remove block"
                                        aria-label={`Remove ${DAY_NAMES[day.day_of_week]} ${formatBlockTime(window.start_time)} to ${formatBlockTime(window.end_time)}`}
                                        onPointerDown={(event) => event.stopPropagation()}
                                        onClick={() => removeWindow(day.day_of_week, windowIndex)}
                                    >
                                        x
                                    </button>
                                    <button
                                        className={`${styles.resize} ${styles.resizeEnd}`}
                                        type="button"
                                        aria-label={`Resize ${DAY_NAMES[day.day_of_week]} availability end`}
                                        onPointerDown={(event) => startAdjusting(event, day.day_of_week, windowIndex, window, "resize-end")}
                                    />
                                </div>
                            );
                        })}

                        {interaction?.type === "create" && interaction.dayOfWeek === day.day_of_week && (
                            <div
                                className={`${styles.block} ${styles.preview}`}
                                style={{
                                    top: interaction.preview.startSlot * SLOT_HEIGHT,
                                    height: (interaction.preview.endSlot - interaction.preview.startSlot) * SLOT_HEIGHT,
                                }}
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className={styles.mobileSchedule}>
                {days.map((day) => (
                    <section className={styles.mobileDay} key={day.day_of_week}>
                        <div className={styles.mobileHeader}>
                            <strong>{DAY_NAMES[day.day_of_week]}</strong>
                            <button type="button" onClick={() => addMobileWindow(day.day_of_week)}>Add hours</button>
                        </div>
                        {day.windows.length === 0 ? (
                            <p>Unavailable</p>
                        ) : (
                            day.windows.map((window, windowIndex) => (
                                <div className={styles.mobileWindow} key={`${window.start_time}-${window.end_time}-${windowIndex}`}>
                                    <input
                                        type="time"
                                        step={SLOT_MINUTES * 60}
                                        value={window.start_time}
                                        aria-label={`${DAY_NAMES[day.day_of_week]} start time`}
                                        onChange={(event) => replaceWindow(day.day_of_week, windowIndex, { ...window, start_time: event.target.value }, false)}
                                        onBlur={() => updateDayWindows(day.day_of_week, day.windows)}
                                    />
                                    <span>to</span>
                                    <input
                                        type="time"
                                        step={SLOT_MINUTES * 60}
                                        value={window.end_time}
                                        aria-label={`${DAY_NAMES[day.day_of_week]} end time`}
                                        onChange={(event) => replaceWindow(day.day_of_week, windowIndex, { ...window, end_time: event.target.value }, false)}
                                        onBlur={() => updateDayWindows(day.day_of_week, day.windows)}
                                    />
                                    <button
                                        type="button"
                                        className={styles.mobileRemove}
                                        aria-label={`Remove ${DAY_NAMES[day.day_of_week]} hours`}
                                        onClick={() => removeWindow(day.day_of_week, windowIndex)}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))
                        )}
                    </section>
                ))}
            </div>
        </AnimatedReveal>
    );
}

export default WeeklyAvailabilityEditor;